/**
 * Áreas do sistema que o usuário escolhe no botão "+" do chat ("quero
 * trabalhar em WhatsApp", "em CRM"…).
 *
 * O foco é uma DICA, não um filtro duro: as ferramentas da área escolhida são
 * destacadas no prompt e as sugestões da tela mudam, mas o catálogo inteiro
 * continua disponível. Filtrar de verdade economizaria contexto e criaria um
 * beco sem saída — quem está em "WhatsApp" e pergunta do funil receberia um
 * "não consigo" em vez da resposta.
 *
 * `permissao` esconde a área de quem não a alcança; `telas` e `ferramentas`
 * entram no bloco de foco do system prompt; `sugestoes` viram os cartões
 * clicáveis do chat.
 */

export type ModuloAssistente = {
  id: string;
  nome: string;
  /** Uma frase: o que dá para resolver aqui. Vai para o prompt e para a UI. */
  resumo: string;
  /** Nome do ícone lucide usado no menu (resolvido no client). */
  icone: string;
  permissao?: string;
  telas: string[];
  ferramentas: string[];
  sugestoes: string[];
};

export const ASSISTENTE_MODULOS: ModuloAssistente[] = [
  {
    id: "whatsapp",
    nome: "WhatsApp",
    resumo: "Transmissões, templates aprovados, fluxos (robôs de conversa), automações e relatórios de entrega.",
    icone: "MessageCircle",
    permissao: "whatsapp:view_broadcasts",
    telas: ["/whatsapp", "/broadcast", "/templates", "/whatsapp-flows", "/automations", "/whatsapp/reports"],
    ferramentas: [
      "campaigns_whatsapp_list", "whatsapp_campaign_create", "whatsapp_campaign_schedule", "campaigns_list",
      "campaign_report", "templates_list", "whatsapp_template_create", "whatsapp_template_submit",
      "flows_list", "flow_get", "whatsapp_flow_create", "flow_set_status",
      "automations_list", "automation_create", "automation_set_active",
    ],
    sugestoes: [
      "Quais transmissões estão em rascunho?",
      "Monte um fluxo de boas-vindas com dois botões",
      "Por que a última transmissão teve tanta falha de entrega?",
      "Crie um template de divulgação e envie para aprovação da Meta",
    ],
  },
  {
    id: "atendimento",
    nome: "Atendimento",
    resumo: "Caixa de entrada unificada: conversas abertas, quem está sem agente, responder e atribuir.",
    icone: "Inbox",
    permissao: "inbox:view",
    telas: ["/inbox", "/inbox/reports"],
    ferramentas: [
      "conversations_list", "conversation_messages", "conversation_assign", "inbox_open_conversation",
      "inbox_send_message", "inbox_add_note", "inbox_report_by_agent",
    ],
    sugestoes: [
      "Resuma esta conversa e diga o que falta responder",
      "Quais conversas estão sem agente agora?",
      "Redija uma resposta para este cliente",
      "Quanto tempo estamos levando para responder?",
    ],
  },
  {
    id: "email",
    nome: "E-mail",
    resumo: "Campanhas de e-mail: criar, testar, enviar, e ler aberturas, cliques e geografia.",
    icone: "Mail",
    permissao: "email:view_campaigns",
    telas: ["/emails", "/emails/templates", "/emails/higiene", "/flows"],
    ferramentas: [
      "email_campaigns_list", "email_campaign_create", "email_campaign_get", "email_campaign_update",
      "email_campaign_import_html", "email_campaign_send_test", "email_campaign_send", "email_campaign_stats",
      "email_campaign_report", "email_campaign_geo_summary", "email_campaigns_geo_compare", "email_campaign_export",
    ],
    sugestoes: [
      "Como foi o desempenho da última campanha de e-mail?",
      "Crie uma campanha de e-mail para divulgar um curso",
      "De quais cidades vieram mais cliques?",
    ],
  },
  {
    id: "instagram",
    nome: "Instagram",
    resumo: "Comentários dos posts (responder, ocultar, arquivar) e automações de palavra-chave para DM.",
    icone: "Instagram",
    permissao: "instagram:view_comments",
    telas: ["/instagram/comments", "/instagram/automations", "/conectar-meta"],
    ferramentas: [
      "instagram_comments_list", "instagram_comments_summary", "instagram_comment_reply",
      "instagram_comment_private_reply", "instagram_comment_set_hidden", "instagram_comment_archive",
      "instagram_media_list", "instagram_automations_list", "instagram_automation_logs",
    ],
    sugestoes: [
      "Tem comentário sem resposta no Instagram?",
      "Qual post teve mais comentários este mês?",
      "Como automatizo comentário com palavra-chave para DM?",
    ],
  },
  {
    id: "ads",
    nome: "Anúncios (Facebook e Instagram)",
    resumo: "Campanhas de Meta Ads: resultados, gasto, criar e pausar anúncios, e de onde vieram os leads.",
    icone: "Megaphone",
    permissao: "ads:view",
    telas: ["/ads", "/ads/new", "/ads/atribuicao", "/conectar-meta"],
    ferramentas: [
      "meta_list_ad_accounts", "meta_list_campaigns", "meta_get_campaign", "meta_list_adsets", "meta_list_ads",
      "meta_get_insights", "meta_create_campaign", "meta_create_adset", "meta_create_ad", "meta_create_ad_creative",
      "meta_update_campaign", "meta_update_adset", "meta_update_ad", "meta_set_status", "meta_search_targeting",
      "meta_upload_image", "meta_list_pages",
    ],
    sugestoes: [
      "Quanto gastamos em anúncios este mês e qual o resultado?",
      "Qual campanha está com o pior custo por resultado?",
      "Pause a campanha que está gastando sem converter",
    ],
  },
  {
    id: "crm",
    nome: "CRM e vendas",
    resumo: "Funil de oportunidades, tarefas, agenda de reuniões, produtos e propostas.",
    icone: "Target",
    permissao: "crm:deals_view",
    telas: ["/crm/deals", "/crm/tasks", "/agenda", "/crm/proposals", "/crm/reports", "/crm/prospecting"],
    ferramentas: [
      "deals_list", "deal_get", "deal_create", "deal_update", "deal_change_stage", "deal_add_note",
      "crm_pipelines_list", "crm_pipeline_summary", "tasks_list", "task_create", "task_update",
      "agenda_list", "agenda_free_slots", "agenda_create",
    ],
    sugestoes: [
      "Quais oportunidades estão paradas há mais de 7 dias?",
      "Quanto tem em aberto no funil por etapa?",
      "Marque uma reunião amanhã às 10h com um contato",
      "Crie uma tarefa de follow-up para hoje",
    ],
  },
  {
    id: "contatos",
    nome: "Contatos",
    resumo: "Base de contatos, tags, segmentos, histórico de cada pessoa e lead scoring.",
    icone: "Users",
    permissao: "contacts:view",
    telas: ["/contacts", "/tags", "/segments", "/lead-scoring"],
    ferramentas: [
      "contacts_search", "contact_get", "contact_create", "contact_update", "contact_timeline",
      "contact_add_tag", "contact_remove_tag", "tags_list", "tag_create", "segments_list", "lead_scoring_hot_leads",
    ],
    sugestoes: [
      "Quais são os leads mais quentes agora?",
      "Quantos contatos temos por tag?",
      "Crie a tag Cliente e me explique como aplicar em massa",
    ],
  },
  {
    id: "inteligencia",
    nome: "Inteligência",
    resumo: "Base de conhecimento da empresa (documentos, produtos, públicos, voz da marca) e os agentes de IA.",
    icone: "Brain",
    permissao: "whatsapp:knowledge_base",
    telas: ["/intelligence", "/agents", "/llm-credentials", "/routing-metrics"],
    ferramentas: ["kb_search"],
    sugestoes: [
      "O que já temos na base de conhecimento?",
      "O que a base diz sobre nosso produto principal?",
      "Como cadastro um novo documento e faço a IA usar?",
      "Como crio um agente de IA para o atendimento?",
    ],
  },
  {
    id: "propostas",
    nome: "Propostas e Canva",
    resumo: "Propostas comerciais em PDF a partir de modelos, inclusive desenhos do Canva, e envio para assinatura.",
    icone: "FileSignature",
    permissao: "crm:proposals_view",
    telas: ["/crm/proposals", "/crm/products", "/settings"],
    ferramentas: [],
    sugestoes: [
      "Como gero uma proposta em PDF para uma oportunidade?",
      "Como conecto o Canva para usar meus modelos?",
      "Quais produtos posso colocar numa proposta?",
    ],
  },
  {
    id: "copiloto",
    nome: "Copiloto de call",
    resumo: "Sugestão de script ao vivo na reunião: playbook, política de disparo e insights das calls.",
    icone: "Headset",
    permissao: "copilot:use",
    telas: ["/copiloto", "/copiloto/insights"],
    ferramentas: ["copilot_playbook_list", "copilot_playbook_add", "copilot_playbook_update", "copilot_settings_get"],
    sugestoes: [
      "O que já tem no playbook do copiloto?",
      "Leia esta proposta e cadastre os trechos no playbook",
      "Cadastre uma resposta para a objeção de preço",
      "Como ligo o copiloto na minha próxima call?",
    ],
  },
  {
    id: "configuracoes",
    nome: "Configurações e integrações",
    resumo: "Empresa, usuários e perfis, conexão com WhatsApp/Meta, e-mail, webhooks e chaves de IA.",
    icone: "Settings",
    permissao: "settings:view",
    telas: ["/settings", "/roles", "/conectar-meta", "/conectar-ia", "/llm-credentials", "/whatsapp-status"],
    ferramentas: [],
    sugestoes: [
      "Como conecto meu número do WhatsApp?",
      "Como dou acesso a um novo usuário da equipe?",
      "Como uso o LeadConversa dentro do Claude ou do ChatGPT?",
    ],
  },
];

export function moduloPorId(id: string | null | undefined): ModuloAssistente | undefined {
  if (!id) return undefined;
  return ASSISTENTE_MODULOS.find((m) => m.id === id);
}

export function modulosPara(permissoes: Set<string>): ModuloAssistente[] {
  return ASSISTENTE_MODULOS.filter((m) => !m.permissao || permissoes.has(m.permissao));
}

/** Sugestões da tela inicial quando nenhuma área foi escolhida. */
export const SUGESTOES_GERAIS = [
  "O que preciso resolver hoje?",
  "Quais conversas estão sem agente agora?",
  "Quais oportunidades estão paradas há mais de 7 dias?",
  "Monte um fluxo de boas-vindas no WhatsApp",
];
